/**
 * Staff-module-specific data extraction and transformation utilities.
 * Not a duplicate of src/utils/dataUtils.js: extractApiData here also unwraps
 * paginated Laravel responses ({ data: { data: [...] } }), which the global
 * version does not handle, and the transform/create helpers below are
 * hardcoded to the employee/role/permission shapes used by this module.
 */

/**
 * Extracts data from various API response structures
 * Handles both paginated and non-paginated responses
 * 
 * Paginated response structure (Laravel):
 * { status: true, message: "...", data: { current_page: 1, data: [...], total: 13, ... } }
 * 
 * @param {any} apiResponse - The API response data
 * @returns {Array} - Extracted data array
 */
export const extractApiData = (apiResponse) => {
  if (!apiResponse) return [];
  
  // Handle paginated Laravel response: { data: { data: [...], current_page, total, ... } }
  if (apiResponse?.data?.data && Array.isArray(apiResponse.data.data)) {
    return apiResponse.data.data;
  }
  
  // Handle different possible API response structures
  if (apiResponse?.["0"]?.original?.data) {
    // Structure: { status: true, "0": { original: { data: [...] } } }
    return apiResponse["0"].original.data;
  } else if (apiResponse?.data && Array.isArray(apiResponse.data)) {
    // Structure: { data: [...] }
    return apiResponse.data;
  } else if (Array.isArray(apiResponse)) {
    // Structure: [...]
    return apiResponse;
  } else if (apiResponse && typeof apiResponse === 'object' && apiResponse.id) {
    // Structure: Single object { id: 6, name: "Production Manager", description: "Manages production" }
    return [apiResponse];
  }
  
  return [];
};

/**
 * Extracts pagination info from Laravel paginated API response
 * @param {any} apiResponse - The API response data
 * @returns {Object} - Pagination info object
 */
export const extractPaginationInfo = (apiResponse) => {
  if (!apiResponse) {
    return {
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1,
      from: 0,
      to: 0,
    };
  }

  // Handle paginated Laravel response: { data: { current_page, per_page, total, ... } }
  const paginationData = apiResponse?.data || apiResponse;
  
  return {
    current_page: paginationData?.current_page || 1,
    per_page: paginationData?.per_page || 10,
    total: paginationData?.total || 0,
    last_page: paginationData?.last_page || 1,
    from: paginationData?.from || 0,
    to: paginationData?.to || 0,
  };
};

/**
 * Transforms employee data for table display
 * @param {Array} rawEmployeeData - Raw employee data from API
 * @returns {Array} - Transformed employee data
 */
export const transformEmployeeData = (rawEmployeeData) => {
  return rawEmployeeData?.map((employee) => ({
    key: employee.id.toString(),
    id: employee.id,
    name: `${employee.first_name} ${employee.middle_name ? employee.middle_name + ' ' : ''}${employee.last_name}`,
    email: employee.email,
    employeeId: employee.staff_id,
    phoneNumber: employee.phone,
    branchs: employee.locations || [],
    teams: employee.teams || [],
    roles: employee.roles || [],
    status: employee.status === 1 ? 'Active' : 'Inactive',
    avatar: employee.avatar,
    dateOfBirth: employee.date_of_birth,
    gender: employee.gender,
    address: employee.address,
    createdAt: employee.created_at,
    updatedAt: employee.updated_at,
    // Keep original data for edit operations
    originalData: employee
  })) || [];
};

/**
 * Transforms role data for table display
 * @param {Array} rawRolesData - Raw roles data from API
 * @returns {Array} - Transformed roles data
 */
export const transformRoleData = (rawRolesData) => {
  return rawRolesData?.map((role) => ({
    key: role.id.toString(),
    id: role.id,
    name: role.name || 'N/A',
    description: role.description || 'No description available',
    permissions: role.permissions || [],
    createdAt: role.created_at,
    updatedAt: role.updated_at,
    // Keep original data for edit operations
    originalData: role
  })) || [];
};

/**
 * Transforms staff count data for dashboard cards
 * Handles both paginated and non-paginated responses
 * @param {any} apiResponse - The API response containing count data
 * @returns {Object} - Transformed count data
 */
export const transformStaffCountData = (apiResponse) => {
  // Handle paginated response - get total from pagination
  if (apiResponse?.data?.total !== undefined) {
    return {
      total: apiResponse.data.total,
      totalActive: apiResponse.data.total, // Adjust if API provides this
      totalInactive: 0
    };
  }
  
  // Handle legacy response structure
  const count = apiResponse?.[0]?.original ?? {};
  return {
    total: count?.total ?? 0,
    totalActive: count?.total_active ?? 0,
    totalInactive: count?.total_inactive ?? 0
  };
};

/**
 * Transforms role count data for dashboard cards
 * @param {Array} rawRolesData - Raw roles data from API
 * @returns {Object} - Transformed role count data
 */
export const transformRoleCountData = (rawRolesData) => {
  if (!rawRolesData || !Array.isArray(rawRolesData)) {
    return {
      total: 0,
      totalActive: 0,
      totalInactive: 0
    };
  }

  return {
    total: rawRolesData.length,
    totalActive: rawRolesData.length, // Assuming all roles are active by default
    totalInactive: 0 // Assuming no inactive roles for now
  };
};

/**
 * Transforms permission data for table display
 * @param {Array} rawPermissionsData - Raw permissions data from API
 * @returns {Array} - Transformed permissions data
 */
/**
 * Transform permission data from API response to UI format
 * @param {Array|Object} rawData - Raw permissions data from API
 * @returns {Array} Transformed permissions array
 */
export const transformPermissionData = (rawData) => {
  // Handle null/undefined
  if (!rawData) {
    return [];
  }

  // If it's already an array, process it
  if (Array.isArray(rawData)) {
    return rawData.map((permission) => ({
      key: permission.id.toString(),
      id: permission.id,
      name: permission.name || 'N/A',
      description: permission.description || 'No description available',
      createdAt: permission.created_at,
      updatedAt: permission.updated_at,
      // Keep original data for edit operations
      originalData: permission
    }));
  }

  // If it's an object with nested data, try to extract the array
  // Common API response patterns
  if (rawData.data && Array.isArray(rawData.data)) {
    return transformPermissionData(rawData.data);
  }
  
  if (rawData.results && Array.isArray(rawData.results)) {
    return transformPermissionData(rawData.results);
  }
  
  if (rawData.permissions && Array.isArray(rawData.permissions)) {
    return transformPermissionData(rawData.permissions);
  }

  // If it's a single object, wrap it in an array
  if (typeof rawData === 'object') {
    return transformPermissionData([rawData]);
  }
  return [];
};

/**
 * Creates form data object from employee data for edit mode
 * Handles both single values and arrays for team and location
 * @param {Object} employeeData - Employee data object
 * @returns {Object} - Form data object
 */
export const createEmployeeFormData = (employeeData) => {
  if (!employeeData) return {};
  
  // Helper to extract IDs from relationship data
  const extractIds = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) {
      return data.map(item => typeof item === 'object' ? item.id : item);
    }
    return typeof data === 'object' ? [data.id] : [data];
  };
  
  return {
    firstName: employeeData.first_name || '',
    middleName: employeeData.middle_name || '',
    lastName: employeeData.last_name || '',
    email: employeeData.email || '',
    phoneNumber: employeeData.phone || '',
    gender: employeeData.gender || '',
    dateOfBirth: employeeData.date_of_birth,
    
    // Extract team IDs - handle various formats from API
    team: extractIds(employeeData.teams || employeeData.team || employeeData.team_id || employeeData.team_ids),
    
    role: employeeData.role_id || employeeData.roles?.[0]?.id || null,
    address: employeeData.address || '',
    
    // Extract location IDs - handle various formats from API
    locationId: extractIds(employeeData.locations || employeeData.location || employeeData.location_id || employeeData.location_ids)
  };
};

/**
 * Transforms form data to API format
 * CRITICAL: Ensures team_ids and location_ids are always arrays
 * @param {Object} formValues - Form values
 * @returns {Object} - API format data
 */
export const transformFormDataToApi = (formValues) => {
  // Helper function to ensure value is an array
  const ensureArray = (value) => {
    if (value === null || value === undefined) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  };

  // Handle avatar - can be a string URL (from handleSubmit) or array (from form)
  let avatarValue = '';
  
  if (typeof formValues.avatar === 'string') {
    // Already a URL string (passed from handleSubmit after Cloudinary upload)
    avatarValue = formValues.avatar;
  } else if (Array.isArray(formValues.avatar) && formValues.avatar.length > 0) {
    // Array from form - extract URL from response or existing url
    avatarValue = formValues.avatar[0]?.response || formValues.avatar[0]?.url || '';
  }


  // Transform data with arrays for team_ids and location_ids
  const apiData = {
    first_name: formValues.firstName,
    last_name: formValues.lastName,
    middle_name: formValues.middleName || '',
    avatar: avatarValue,
    gender: formValues.gender,
    email: formValues.email,
    phone: formValues.phoneNumber,
    address: formValues.address,
    
    // CRITICAL: Convert to arrays
    // This ensures backend receives [16] or [7, 16] instead of just 16
    team_ids: ensureArray(formValues.team).map(id => parseInt(id)),
    location_ids: ensureArray(formValues.locationId).map(id => parseInt(id)),
    
    role_id: formValues.role ? parseInt(formValues.role) : null,
    date_of_birth: formValues.dateOfBirth ? formValues.dateOfBirth.format('YYYY-MM-DD') : ''
  };

  // Remove empty strings and null values if needed
  Object.keys(apiData).forEach(key => {
    if (apiData[key] === '' || apiData[key] === null) {
      delete apiData[key];
    }
  });

  return apiData;
};

/**
 * Transform permission count data
 * @param {Array} permissionsData - Permissions data
 * @returns {Object} - Count data
 */
export const transformPermissionCountData = (permissionsData) => {
  if (!Array.isArray(permissionsData)) {
    return {
      total: 0,
      assigned: 0,
      unassigned: 0,
    };
  }

  const total = permissionsData.length;
  const assigned = permissionsData.filter(permission => 
    permission.is_assigned === true || 
    permission.is_assigned === 1 || 
    permission.assigned === true ||
    permission.assigned === 1
  ).length;
  const unassigned = total - assigned;

  return {
    total,
    assigned,
    unassigned,
  };
};